//nơi lọc các dữ liệu
import React, { useState, useEffect } from "react";
import "./FilterPanel.css"; // File CSS riêng
import { FunnelIcon, XCircleIcon, MapPinIcon, CurrencyDollarIcon } from "@heroicons/react/24/outline";

const FilterPanel = ({
  category,
  activeSubCategoryID,
  onSubCategoryChange,
  activeProvinces,
  onProvinceChange,
  priceRange,
  onPriceChange,
  condition,
  onConditionChange,
  onResetFilters,
}) => {
  const [provinces, setProvinces] = useState([]);
  const [showPriceDropdown, setShowPriceDropdown] = useState(false);
  const [showConditionDropdown, setShowConditionDropdown] = useState(false);

  // Fetch provinces từ API districts
  useEffect(() => {
    const fetchProvinces = async () => {
      try {
        const response = await fetch("https://iudi-api.thinkdiff.us/api/districts");
        const result = await response.json();

        let districts = [];
        if (Array.isArray(result)) {
          districts = result;
        } else if (result && Array.isArray(result.data)) {
          districts = result.data;
        }

        // Extract unique provinces
        const provinceMap = new Map();
        districts.forEach(district => {
          if (district.ProvinceID && district.ProvinceName) {
            if (!provinceMap.has(district.ProvinceID)) {
              provinceMap.set(district.ProvinceID, {
                ProvinceID: district.ProvinceID,
                ProvinceName: district.ProvinceName
              });
            }
          }
        });

        const uniqueProvinces = Array.from(provinceMap.values())
          .sort((a, b) => a.ProvinceName.localeCompare(b.ProvinceName, 'vi'));

        // Lấy top 4 provinces phổ biến
        const topProvinces = uniqueProvinces.filter(p =>
          ['Hà Nội', 'Hồ Chí Minh', 'Đà Nẵng', 'Cần Thơ'].some(name =>
            p.ProvinceName.includes(name)
          )
        );

        setProvinces(topProvinces);
      } catch (error) {
        console.error("Error fetching provinces:", error);
      }
    };

    fetchProvinces();
  }, []);

  const handleProvinceToggle = (provinceId) => {
    if (activeProvinces.includes(provinceId)) {
      onProvinceChange(activeProvinces.filter(id => id !== provinceId));
    } else {
      onProvinceChange([...activeProvinces, provinceId]);
    }
  };

  // Icon mapping cho subcategories
  const getSubcategoryIcon = (categoryName) => {
    const name = categoryName.toLowerCase();
    if (name.includes('tủ lạnh') || name.includes('tu lanh')) return '❄️';
    if (name.includes('máy lạnh') || name.includes('điều hòa')) return '🌬️';
    if (name.includes('máy giặt')) return '🧺';
    if (name.includes('điện thoại') || name.includes('phone')) return '📱';
    if (name.includes('laptop') || name.includes('máy tính')) return '💻';
    if (name.includes('xe') || name.includes('ô tô')) return '🚗';
    if (name.includes('nhà') || name.includes('đất')) return '🏠';
    if (name.includes('thời trang') || name.includes('quần áo')) return '👕';
    if (name.includes('giày') || name.includes('dép')) return '👟';
    return '📦';
  };

  return (
    <div className="filter-panel-wrapper">
      {/* Tiêu đề */}
      <div className="filter-panel-header">
        <h1 className="title">
          {category.CategoryName} cũ giá rẻ, bền tốt 12/2024
        </h1>
      </div>

      {/* Hàng 1 - Lọc chính */}
      <div className="filter-row">
        <button className="filter-pill-btn">
          <FunnelIcon className="icon" /> Lọc
        </button>

        {/* Danh mục con dropdown */}
        {category.Children && category.Children.length > 0 && (
          <select
            className="filter-pill-select"
            value={activeSubCategoryID || ''}
            onChange={(e) => onSubCategoryChange(e.target.value ? parseInt(e.target.value) : null)}
          >
            <option value="">Tất cả danh mục</option>
            {category.Children.map(sub => (
              <option key={sub.CategoryID} value={sub.CategoryID}>
                {sub.CategoryName}
              </option>
            ))}
          </select>
        )}

        {/* Giá dropdown */}
        <div className="filter-dropdown-wrapper">
          <button
            className="filter-pill-btn"
            onClick={() => {
              setShowPriceDropdown(!showPriceDropdown);
              setShowConditionDropdown(false);
            }}
          >
            Giá
          </button>
          {showPriceDropdown && (
            <div className="dropdown-menu">
              <input
                type="number"
                placeholder="Giá từ"
                value={priceRange.min}
                onChange={(e) => onPriceChange({ ...priceRange, min: e.target.value })}
                className="price-input"
              />
              <input
                type="number"
                placeholder="Giá đến"
                value={priceRange.max}
                onChange={(e) => onPriceChange({ ...priceRange, max: e.target.value })}
                className="price-input"
              />
            </div>
          )}
        </div>

        {/* Tình trạng dropdown */}
        <div className="filter-dropdown-wrapper">
          <button
            className="filter-pill-btn"
            onClick={() => {
              setShowConditionDropdown(!showConditionDropdown);
              setShowPriceDropdown(false);
            }}
          >
            Tình trạng
          </button>
          {showConditionDropdown && (
            <div className="dropdown-menu">
              <button
                className={`dropdown-item ${condition === 'new' ? 'active' : ''}`}
                onClick={() => {
                  onConditionChange(condition === 'new' ? null : 'new');
                  setShowConditionDropdown(false);
                }}
              >
                Mới
              </button>
              <button
                className={`dropdown-item ${condition === 'used' ? 'active' : ''}`}
                onClick={() => {
                  onConditionChange(condition === 'used' ? null : 'used');
                  setShowConditionDropdown(false);
                }}
              >
                Đã sử dụng
              </button>
            </div>
          )}
        </div>

        <button onClick={onResetFilters} className="filter-pill-btn reset-filter">
          Xóa lọc
        </button>
      </div>

      {/* Hàng 2 - Lọc khu vực (Pill buttons) */}
      <div className="filter-row location-row">
        <span className="location-label">Khu vực:</span>
        <div className="location-pills">
          {provinces.map(province => (
            <button
              key={province.ProvinceID}
              className={`location-btn ${activeProvinces.includes(province.ProvinceID) ? 'active' : ''}`}
              onClick={() => handleProvinceToggle(province.ProvinceID)}
            >
              {province.ProvinceName}
            </button>
          ))}
          <button className="location-btn">
            <MapPinIcon className="icon-small" /> Gần tôi
          </button>
        </div>
      </div>

      {/* Hàng 3 - Danh mục con với icons */}
      {category.Children && category.Children.length > 0 && (
        <div className="subcategory-row">
          {category.Children.map((subCat) => (
            <button
              key={subCat.CategoryID}
              className={`subcategory-btn ${activeSubCategoryID === subCat.CategoryID ? "active" : ""
                }`}
              onClick={() =>
                onSubCategoryChange(
                  subCat.CategoryID === activeSubCategoryID
                    ? null
                    : subCat.CategoryID
                )
              }
            >
              <span className="subcategory-icon">{getSubcategoryIcon(subCat.CategoryName)}</span>
              <span className="subcategory-name">{subCat.CategoryName}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default FilterPanel;